import { usePublicPracticeBranding } from "@/hooks/usePublicPracticeBranding";

export interface PracticeLogoProps {
  /** Size: "sm" | "md" | "lg" | "xl" | "header" | "hero" */
  size?: "sm" | "md" | "lg" | "xl" | "header" | "hero";
  /** Show text label next to the logo */
  showText?: boolean;
  /** Additional class names */
  className?: string;
}

const sizeMap = {
  sm: { container: "w-7 h-7", text: "text-[13px]", icon: "text-sm" },
  md: { container: "w-8 h-8", text: "text-[15px]", icon: "text-base" },
  lg: { container: "w-12 h-12", text: "text-[18px]", icon: "text-xl" },
  xl: { container: "w-full h-full", text: "text-[42px]", icon: "text-4xl" },
};

/**
 * PracticeLogo — shows the practice logo if admin has uploaded one,
 * otherwise shows a tasteful "Perspective Therapy" text fallback.
 *
 * Logo URL is managed by admin on the Practice Settings page.
 * When no logo is uploaded, admin sees a dashed upload placeholder in Practice Settings.
 */
export default function PracticeLogo({
  size = "md",
  showText = true,
  className = "",
}: PracticeLogoProps) {
  const { logoUrl, practiceName, clearLogo } = usePublicPracticeBranding();
  const handleLogoError = clearLogo;

  // ── Hero size — used on the login page, generous and prominent ──
  if (size === "hero") {
    if (logoUrl) {
      return (
        <div className={`flex items-center justify-center ${className}`}>
          <img
            alt={practiceName}
            className="w-[160px] sm:w-[190px] md:w-[220px] h-auto max-h-[140px] object-contain"
            src={logoUrl}
            onError={handleLogoError}
          />
        </div>
      );
    }
    // No logo — polished text fallback as the brand mark
    return (
      <div className={`flex flex-col items-center justify-center ${className}`}>
        <span className="font-fraunces font-bold tracking-tight text-brand-dark text-[36px] sm:text-[42px] md:text-[52px] leading-tight">
          {practiceName}
        </span>
      </div>
    );
  }

  // ── Header size — used in navigation bars, compact but readable ──
  if (size === "header") {
    if (logoUrl) {
      return (
        <div className={`flex items-center gap-2.5 ${className}`}>
          <img
            alt={practiceName}
            className="max-w-[110px] sm:max-w-[130px] md:max-w-[150px] h-auto max-h-[40px] sm:max-h-[42px] md:max-h-[44px] object-contain"
            src={logoUrl}
            onError={handleLogoError}
          />
          {showText && (
            <span className="font-fraunces font-bold tracking-tight text-brand-dark whitespace-nowrap text-[16px] sm:text-[17px] md:text-[18px]">
              {practiceName}
            </span>
          )}
        </div>
      );
    }
    return (
      <div className={`flex items-center gap-2.5 ${className}`}>
        <div className="w-8 h-8 flex items-center justify-center shrink-0 rounded-lg border border-dashed border-brand-dark/15 bg-brand-dark/[0.02]">
          <i className="ri-building-line text-brand-dark/25 text-sm" />
        </div>
        {showText && (
          <span className="font-fraunces font-bold tracking-tight text-brand-dark whitespace-nowrap text-[16px] sm:text-[17px] md:text-[18px]">
            {practiceName}
          </span>
        )}
      </div>
    );
  }

  // ── Legacy square sizes (sm / md / lg / xl) ──
  const dims = sizeMap[size];
  const isXl = size === "xl";

  // Has an uploaded logo — show the image
  if (logoUrl) {
    return (
      <div className={`flex items-center gap-2.5 ${className}`}>
        <div className={`${dims.container} flex items-center justify-center shrink-0`}>
          <img
            alt={practiceName}
            className="w-full h-full object-contain"
            src={logoUrl}
            onError={handleLogoError}
          />
        </div>
        {showText && !isXl && (
          <span className={`font-fraunces font-bold tracking-tight text-brand-dark whitespace-nowrap ${dims.text}`}>
            {practiceName}
          </span>
        )}
      </div>
    );
  }

  // No logo uploaded — show placeholder
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <div className={`${dims.container} flex items-center justify-center shrink-0 rounded-xl border-2 border-dashed border-brand-dark/15 bg-brand-dark/[0.02]`}>
        <i className={`ri-building-line text-brand-dark/25 ${dims.icon}`} />
      </div>
      {showText && (
        <span className={`font-fraunces font-bold tracking-tight text-brand-dark whitespace-nowrap ${!isXl ? dims.text : ""}`}>
          {practiceName}
        </span>
      )}
    </div>
  );
}
